// src/utils/queue.ts
// M5-QUEUE (20.09.2026): очередь отложенной отметки дня. Первый заход из
// двух — механизм без имитации на экране (второй заход покажет очередь и
// отклонённые записи человеку).
//
// QUEUE-UI-A (21.09.2026): второй заход, часть «а» — основа имитации.
// Добавлены localDate/xp на записи (для закраски дня и будущего начисления
// в части «б»), события queue:changed/queue:synced (открытый экран узнаёт
// о постановке/доставке без ручного опроса) и getPending() для чтения
// очереди по курсу. Классификация ответов, изоляция пользователей, потолок
// попыток — не тронуты этим заходом.
//
// content-access, инвариант 5: отметка дня — мутирующий путь, разбирается
// отдельно от читающих (progress.ts/getDays, content.ts) — здесь только
// доставка того же мутирующего запроса, отложенная во времени.
// content-access, инвариант 9: отметка, отложенная на устройстве в этой
// очереди, — способность, отложенная от проверки права. Очередь НЕ
// проверяет право сама и не имитирует проверку — она просто откладывает
// вызов того же /api/progress/complete-day, а весь набор проверок права
// (status='active', диапазон дня, грейс) сервер выполняет заново в момент
// фактической доставки, как и для прямого онлайн-вызова. Разница с
// онлайн-путём только во ВРЕМЕНИ вызова, не в объёме проверки.
// content-access, инвариант 8: localDate ниже — темп/отображение (какой
// день закрасить офлайн), не право. Источники (tz, morning_hour_effective)
// — те же, что использует сервер при доставке (QUEUE-UI-A, Шаг 1); при
// расхождении с фактической доставкой прогноз на экране поправляется
// событием queue:synced, не блокирует и не подменяет серверную проверку.
//
// QUEUE-UI-B (22.09.2026): часть «б» — искорки/бейдж/модалка/плашка
// отклонённых на экране (DashboardScreen.tsx, badges.ts), плюс два фикса
// здесь: закрыто окно отката между доставкой и синхронизацией (Шаг 2 —
// queue:changed на успешной доставке убран, см. комментарий у flushQueue),
// и задержка после 'online' против мигающей сети (Шаг 7, см. низ файла).
// content-access, инвариант 2 (это же имитация DashboardScreen, здесь
// только источники данных для неё): всё, что enqueue/flushQueue отдают
// наружу (localDate/xp записи, события) — прогноз для отображения, не
// решение о праве; право по-прежнему проверяет сервер при каждой реальной
// доставке (инвариант 5, комментарий выше не меняется этим заходом).

import { completeDay, type CompleteDayResult } from "../api/progress";
import { getSnapshot, findEnrollment } from "../api/snapshot";
import { computeLocalDate } from "./localDate";
import { get as storeGet, put as storePut, compositeKey } from "./store";

const QUEUE_KEY = "offline_queue_v1";
const REJECTED_KEY = "offline_queue_rejected_v1";

// Потолок попыток по сети/5xx. Доставка триггерится только событием 'online'
// и стартом приложения (не таймером, см. Шаг 4 задания) — счётчик растёт не
// быстрее реальных переключений связи/перезапусков приложения. 10 даёт запас
// на несколько дней типичных обрывов мобильной сети и при этом не даёт
// записи, упирающейся в постоянный 5xx (например, затяжной сбой похода
// сервера в Directus), расти в очереди бесконечно. Не привязан к серверному
// грейсу (3 дня, api/index.js) напрямую: grace_expired — отдельная, терминальная
// классификация ниже, снимающая запись независимо от числа попыток. Если на
// практике окажется мало (устройство подолгу в самолётном режиме, но
// открывается чаще raз в 1-2 дня) — поднимается без пересмотра остальной
// логики, это чисто защитное число, не часть контракта с сервером.
const MAX_ATTEMPTS = 10;

export type QueueEntry = {
  id: string;
  courseId: number | string;
  completedAt: string;
  dayNumber: number;
  userId: number;
  attempts: number;
  queuedAt: number;
  // QUEUE-UI-A: вычисляются один раз в момент постановки (enqueue), тем же
  // способом, каким сервер посчитает их при фактической доставке (localDate.ts
  // — дословный перенос серверных функций). Запись может не иметь этих полей,
  // если создана до QUEUE-UI-A и всё ещё лежит на устройстве — читатели
  // обязаны трактовать отсутствие как "неизвестно", не как ложь/ноль.
  localDate: string | null;
  // Сумма искорок за день из закэшированного контента дня на момент
  // постановки. Только сохраняется здесь — используется частью «б». Нет в
  // кэше контента на момент постановки (день не был открыт офлайн заранее)
  // → null, не 0 (0 означало бы "день без начисления", это другое утверждение).
  xp: number | null;
};

export type RejectedEntry = QueueEntry & {
  reason: string;
  rejectedAt: number;
};

function notifyChanged(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("queue:changed"));
}

async function readQueue(): Promise<QueueEntry[]> {
  const res = await storeGet<QueueEntry[]>(QUEUE_KEY);
  if (res.status === "empty") return [];
  return Array.isArray(res.data) ? res.data : [];
}

async function writeQueue(list: QueueEntry[]): Promise<void> {
  await storePut(QUEUE_KEY, list, 0);
}

async function readRejected(): Promise<RejectedEntry[]> {
  const res = await storeGet<RejectedEntry[]>(REJECTED_KEY);
  if (res.status === "empty") return [];
  return Array.isArray(res.data) ? res.data : [];
}

async function addRejected(entry: QueueEntry, reason: string): Promise<void> {
  const list = await readRejected();
  list.push({ ...entry, reason, rejectedAt: Date.now() });
  await storePut(REJECTED_KEY, list, 0);
  console.log(`[queue] rejected course=${entry.courseId} day=${entry.dayNumber} reason=${reason}`);
}

/**
 * localDate записи — источники ровно те, что сервер использует при доставке
 * (QUEUE-UI-A, Шаг 1): живой пояс профиля (snapshot.profile.timezone,
 * фолбэк 'Europe/Moscow' — тот же, что серверный liveTz()) и
 * morning_hour_effective/morning_minute_effective конкретного enrollment,
 * не course_settings.morning_hour. Снапшот не спрошен здесь заново по сети —
 * getSnapshot() сам отдаёт кэш при отказе (та же гарантия, что у остального
 * гейта офлайн). Нет снапшота/enrollment вовсе (устройство ни разу не
 * открывало курс при связи) → null, неизвестно.
 */
async function computeEntryLocalDate(
  courseId: number | string,
  completedAtIso: string
): Promise<string | null> {
  try {
    const snapResult = await getSnapshot();
    const snap =
      snapResult.status === "ok" || snapResult.status === "cached" ? snapResult.data : null;
    if (!snap) return null;
    const enr = findEnrollment(snap, courseId);
    if (!enr) return null;
    const tz = snap.profile?.timezone ?? "Europe/Moscow";
    return computeLocalDate(completedAtIso, tz, enr.morning_hour_effective, enr.morning_minute_effective);
  } catch (e) {
    console.error("[queue] localDate compute failed:", e instanceof Error ? e.message : String(e));
    return null;
  }
}

type CachedDayContent = { day: { xp: number | null } | null };

/** xp дня из кэша content.ts (тот же составной ключ, тот же object store — store.ts, compositeKey). */
async function readCachedDayXp(courseId: number | string, dayNumber: number): Promise<number | null> {
  try {
    const key = compositeKey("content_day", courseId, dayNumber);
    const res = await storeGet<CachedDayContent>(key);
    if (res.status === "empty") return null;
    return res.data?.day?.xp ?? null;
  } catch (e) {
    console.error("[queue] xp cache read failed:", e instanceof Error ? e.message : String(e));
    return null;
  }
}

/**
 * Поставить отметку дня в очередь. Дубль (тот же course_id + day_number) не
 * добавляется повторно — идемпотентно относительно повторных офлайн-тапов
 * на одном и том же дне.
 *
 * completedAt — момент фактического нажатия, зафиксированный ВЫЗЫВАЮЩИМ
 * кодом ДО вызова enqueue (см. DashboardScreen.onCompletePractice). Очередь
 * сама НИКОГДА не порождает время — иначе вечерняя офлайн-отметка, досланная
 * утром, засчиталась бы не за тот день (см. QUEUE-RECON, Шаг 3: сервер
 * считает день по completed_at из тела запроса, не по времени получения).
 */
export async function enqueue(entry: {
  courseId: number | string;
  completedAt: string;
  dayNumber: number;
  userId: number;
}): Promise<QueueEntry> {
  const list = await readQueue();
  // QUEUE-UI-B: .find, не .some — на дубле вызывающий код (DashboardScreen)
  // получает ту же уже поставленную запись (с её xp/localDate), а не void.
  const dup = list.find(
    (e) => String(e.courseId) === String(entry.courseId) && e.dayNumber === entry.dayNumber
  );
  if (dup) {
    console.log(`[queue] enqueue skipped, already queued course=${entry.courseId} day=${entry.dayNumber}`);
    return dup;
  }

  const [localDate, xp] = await Promise.all([
    computeEntryLocalDate(entry.courseId, entry.completedAt),
    readCachedDayXp(entry.courseId, entry.dayNumber),
  ]);

  const record: QueueEntry = {
    id: `${entry.courseId}_${entry.dayNumber}_${Date.now()}`,
    courseId: entry.courseId,
    completedAt: entry.completedAt,
    dayNumber: entry.dayNumber,
    userId: entry.userId,
    attempts: 0,
    queuedAt: Date.now(),
    localDate,
    xp,
  };
  list.push(record);
  await writeQueue(list);
  console.log(
    `[queue] enqueued course=${entry.courseId} day=${entry.dayNumber} completed_at=${entry.completedAt} local_date=${localDate ?? "unknown"} xp=${xp ?? "unknown"}`
  );
  notifyChanged();
  return record;
}

// Последний известный user_id — обновляется при каждом вызове flushQueue с
// явным аргументом (App.tsx, после получения снапшота). Обработчик 'online'
// ниже вызывает flushQueue() без аргумента и полагается на это значение —
// без него доставка при чистом событии 'online' не сможет проверить
// изоляцию пользователя и не должна запускаться вовсе (см. flushQueue).
let lastKnownUserId: number | null = null;
let flushing = false;

/**
 * Разобрать очередь: отправить каждую подходящую запись через тот же
 * /api/progress/complete-day, что и прямой онлайн-вызов (api/progress.ts —
 * completeDay). Классификация ответа (Шаг 4 задания):
 *
 *  - 200                  → удалить из очереди, обновить снапшот
 *  - 400 / 403 / 404      → неустранимый отказ: удалить из очереди, в отклонённые
 *  - 401                  → сессия истекла: оставить в очереди, без счёта попыток
 *  - 5xx / сеть (нет status) → оставить, +1 попытка; на потолке — в отклонённые
 *
 * Классификация — по HTTP-статусу ответа, не по конкретному коду ошибки в
 * теле: сервер отдаёт РОВНО такой набор кодов на этих статусах (400:
 * course_id_required/completed_at_required/invalid_completed_at/
 * completed_at_in_future/grace_expired/day_before_start/course_completed;
 * 403: not_active; 404: enrollment_not_found — все три места устройства
 * прочитаны в QUEUE-RECON и в этом заходе), поэтому решение по статусу
 * покрывает весь набор без перечисления строк кодов по одной.
 *
 * Изоляция пользователей — проверяется ПЕРЕД отправкой, не после: запись
 * с чужим userId выбрасывается из очереди молча (не «отклонённая» — это не
 * отказ сервера, а собственная защита очереди от отправки чужой отметки под
 * текущей сессией).
 *
 * queue:changed (QUEUE-UI-A) — при постановке (enqueue) и при отклонении
 * (addRejected). Не при простом росте счётчика попыток и не при молчаливом
 * выбросе чужой записи — ни то, ни другое не меняет то, что видно текущему
 * пользователю в очереди.
 *
 * QUEUE-UI-B, Шаг 2 (найдено этим треком): на успешной доставке
 * queue:changed раньше слался ЗДЕСЬ же, сразу по res.ok — экран (пустая
 * очередь → doneToday/стрик возвращаются к досинхронным серверным
 * значениям) на короткое время показывал откат назад, пока не приходил
 * следующий queue:synced с уже свежими данными. Событие на доставке убрано
 * — единственный сигнал экрану теперь queue:synced ниже, и он приходит
 * только когда снапшот/дни уже свежие (DashboardScreen перечитывает
 * pendingQueue ПОСЛЕ них, одним и тем же обработчиком — см. отчёт
 * QUEUE-UI-B). Подтверждено по коду, не гипотеза: постановка и отклонение
 * не подвержены тому же окну — там queue:changed остаётся единственным
 * источником истины и старого значения, с которым можно разойтись, нет.
 */
export async function flushQueue(currentUserId?: number | null): Promise<void> {
  if (currentUserId !== undefined && currentUserId !== null) {
    lastKnownUserId = currentUserId;
  }
  const userId = currentUserId ?? lastKnownUserId;
  if (userId == null) {
    console.log("[queue] flush skipped: no known user id yet");
    return;
  }
  if (flushing) {
    console.log("[queue] flush skipped: already in progress");
    return;
  }
  flushing = true;
  try {
    let list = await readQueue();
    if (list.length === 0) return;

    let delivered = false;

    for (const entry of [...list]) {
      if (entry.userId !== userId) {
        console.log(
          `[queue] dropped foreign-user entry course=${entry.courseId} day=${entry.dayNumber} entryUser=${entry.userId} currentUser=${userId}`
        );
        list = list.filter((e) => e.id !== entry.id);
        await writeQueue(list);
        continue;
      }

      let res: CompleteDayResult;
      try {
        res = await completeDay(entry.courseId, entry.completedAt);
      } catch {
        res = { ok: false, error: "network_error" };
      }

      if (res.ok) {
        list = list.filter((e) => e.id !== entry.id);
        await writeQueue(list);
        console.log(`[queue] delivered course=${entry.courseId} day=${entry.dayNumber}`);
        delivered = true;
        // QUEUE-UI-B, Шаг 2: queue:changed НЕ шлём здесь — см. комментарий
        // над flushQueue. Экран узнаёт об этой доставке из queue:synced ниже.
        continue;
      }

      const status = res.status;
      const isUnauthorized = status === 401;
      const isTerminal = status === 400 || status === 403 || status === 404;
      const isRetryable = !status || status >= 500;

      if (isUnauthorized) {
        console.log(`[queue] deferred (unauthorized) course=${entry.courseId} day=${entry.dayNumber}`);
        continue;
      }

      if (isTerminal) {
        list = list.filter((e) => e.id !== entry.id);
        await writeQueue(list);
        await addRejected(entry, res.error);
        notifyChanged();
        continue;
      }

      if (isRetryable) {
        const attempts = entry.attempts + 1;
        if (attempts >= MAX_ATTEMPTS) {
          list = list.filter((e) => e.id !== entry.id);
          await writeQueue(list);
          await addRejected(entry, `max_attempts_exceeded:${res.error}`);
          notifyChanged();
          continue;
        }
        list = list.map((e) => (e.id === entry.id ? { ...e, attempts } : e));
        await writeQueue(list);
        console.log(
          `[queue] retry scheduled course=${entry.courseId} day=${entry.dayNumber} attempts=${attempts} reason=${res.error}`
        );
        continue;
      }

      // Не должно встречаться при текущем контракте сервера — не теряем
      // запись молча на неопознанном статусе, оставляем в очереди как есть.
      console.log(
        `[queue] unhandled status=${status} error=${res.error} course=${entry.courseId} day=${entry.dayNumber} — left in queue`
      );
    }

    if (delivered) {
      // queue:synced (QUEUE-UI-A) — только после того, как свежий снапшот
      // РЕАЛЬНО лёг в кэш (status "ok" — getSnapshot() кладёт его в
      // cacheSnapshot() сам, тем же путём, что и обычный онлайн-опрос).
      // "cached"/"unavailable" здесь означают, что доставка прошла, но
      // подтверждённого свежего снимка на руках всё ещё нет — событие в
      // этом случае не шлём: экран остался бы с прогнозом, выданным за
      // подтверждённый факт, если бы подписчик принял старые серверные
      // значения как окончательные.
      const snapResult = await getSnapshot();
      if (snapResult.status === "ok") {
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("queue:synced"));
        }
      } else {
        console.error(`[queue] synced event skipped: snapshot refresh status=${snapResult.status}`);
      }
    }
  } finally {
    flushing = false;
  }
}

export async function getRejected(): Promise<RejectedEntry[]> {
  return readRejected();
}

/**
 * Убрать отклонённые записи курса из хранилища (QUEUE-UI-B, Шаг 6 —
 * закрытие плашки человеком на DashboardScreen). Без этого getRejected()
 * снова отдал бы те же записи при следующем открытии экрана — плашка
 * вернулась бы сама, хотя человек её уже закрыл.
 */
export async function clearRejectedForCourse(courseId: number | string): Promise<void> {
  const list = await readRejected();
  const filtered = list.filter((e) => String(e.courseId) !== String(courseId));
  await storePut(REJECTED_KEY, filtered, 0);
}

export async function getQueueLength(): Promise<number> {
  const list = await readQueue();
  return list.length;
}

/** Записи очереди по конкретному курсу (QUEUE-UI-A) — DashboardScreen читает
 *  их для закраски дня/значка «ждёт отправки», отдельно от остальных курсов. */
export async function getPending(courseId: number | string): Promise<QueueEntry[]> {
  const list = await readQueue();
  return list.filter((e) => String(e.courseId) === String(courseId));
}

// QUEUE-UI-B, Шаг 7: задержка перед доставкой по 'online'. Раньше КАЖДОЕ
// срабатывание 'online' сразу давало полноценную попытку flushQueue() —
// при мигающей сети (граница покрытия мобильной сети, устройство быстро
// теряет/восстанавливает связь) браузер шлёт 'online' на каждое короткое
// восстановление, и потолок MAX_ATTEMPTS=10 на запись исчерпывался за
// минуты вместо расчётных "нескольких дней обрывов" (см. комментарий у
// MAX_ATTEMPTS выше). 5 секунд — компромисс: не настолько долго, чтобы
// человек с реально восстановившейся связью заметил задержку доставки, и
// достаточно, чтобы типичное дребезжание mobile-сети (секунды между
// потерей/восстановлением) схлопнулось в одну попытку вместо нескольких.
// Таймер НЕ перезапускается повторными 'online' в этом окне (это не меняет
// суть — одно окно даёт максимум одну попытку) и НЕ является периодическим
// опросом: он не перезапускает сам себя, а ждёт следующего реального
// события 'online' — запрет из QUEUE-MECH на самостоятельный polling не
// нарушается.
const ONLINE_FLUSH_DELAY_MS = 5000;
let onlineFlushTimer: ReturnType<typeof setTimeout> | null = null;

// Слушатель 'online' — единственный источник доставки помимо явного вызова
// со старта приложения (App.tsx). Модуль — синглтон (ES-модуль кэшируется
// при импорте), поэтому обработчик регистрируется ровно один раз за время
// жизни страницы, независимо от того, сколько экранов импортируют queue.ts.
if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    if (onlineFlushTimer != null) return; // попытка уже назначена — не плодим новых
    onlineFlushTimer = setTimeout(() => {
      onlineFlushTimer = null;
      void flushQueue();
    }, ONLINE_FLUSH_DELAY_MS);
  });
}
